class MobileNavToggle extends HTMLElement {
	constructor() {
		super()
		this.menuDrawer = document.querySelector('.f-drawer-mobile-nav')
		this.openNav = this.openNav.bind(this)
		this.onDrawerClose = this.onDrawerClose.bind(this)
		this.prefetchNavImages = this.prefetchNavImages.bind(this)
		this._hideTimer = null
		this._onHideTransitionEnd = null
		this.addEventListener('click', this.openNav)
		// Start downloads on first intent — pointer/touch beat the open animation.
		this.addEventListener('pointerdown', this.prefetchNavImages, { passive: true })
		this.addEventListener('touchstart', this.prefetchNavImages, { passive: true })

		if (this.menuDrawer) {
			this.menuDrawer.addEventListener('close', this.onDrawerClose)
		}
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.openNav)
		this.removeEventListener('pointerdown', this.prefetchNavImages)
		this.removeEventListener('touchstart', this.prefetchNavImages)
		this.menuDrawer?.removeEventListener('close', this.onDrawerClose)
		this.clearHideWait()
	}

	prefetchNavImages() {
		document.querySelector('mobile-nav-panels')?.prefetchAllImages?.()
	}

	clearHideWait() {
		if (this._hideTimer) {
			window.clearTimeout(this._hideTimer)
			this._hideTimer = null
		}
		if (this.menuDrawer && this._onHideTransitionEnd) {
			this.menuDrawer.removeEventListener(
				'transitionend',
				this._onHideTransitionEnd
			)
			this._onHideTransitionEnd = null
		}
	}

	openNav() {
		if (!this.menuDrawer) return
		// Cancel a pending post-close [hidden] — reopen mid-slide must not hide again.
		this.clearHideWait()
		// Drawer ships with [hidden]. Strip it so the open transition can paint,
		// then open. Idle closed state restores [hidden] after the slide finishes.
		this.menuDrawer.removeAttribute('hidden')
		void this.menuDrawer.offsetWidth
		this.menuDrawer.openDrawer()
		this.setAttribute('aria-expanded', 'true')
	}

	onDrawerClose() {
		this.setAttribute('aria-expanded', 'false')
		this.clearHideWait()
		const drawer = this.menuDrawer
		if (!drawer) return

		let settled = false
		const hide = () => {
			if (settled) return
			settled = true
			this.clearHideWait()
			if (!drawer.hasAttribute('open')) {
				drawer.setAttribute('hidden', '')
			}
		}
		this._onHideTransitionEnd = (evt) => {
			if (evt.target !== drawer) return
			if (evt.propertyName && evt.propertyName !== 'transform') return
			hide()
		}

		// Prefer transitionend so hide lands with the slide; timeout is a safety net
		// (reduced-motion / interrupted close / missing transition).
		drawer.addEventListener('transitionend', this._onHideTransitionEnd)
		this._hideTimer = window.setTimeout(hide, 400)
	}
}
customElements.define('mobile-nav-toggle', MobileNavToggle)

class MobileNavPanels extends HTMLElement {
	constructor() {
		super()
		this.stack = ['root']
		this.isAnimating = false
		this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
		this.onClick = this.onClick.bind(this)
		this.onKeyDown = this.onKeyDown.bind(this)
		this.onDrawerClose = this.onDrawerClose.bind(this)
		this.onDrawerOpen = this.onDrawerOpen.bind(this)
		this.onTouchMoveWhileOpen = this.onTouchMoveWhileOpen.bind(this)
		this.onLeaveIntent = this.onLeaveIntent.bind(this)
		this.onLeaveMove = this.onLeaveMove.bind(this)
		this.onLeaveCancel = this.onLeaveCancel.bind(this)
		this.onPageShow = this.onPageShow.bind(this)
		this._touchLockOn = false
		this.isLeaving = false
		this._leaveCandidate = null
	}

	connectedCallback() {
		this.panelsRoot = this.querySelector('[data-mobile-nav-panels]') || this
		this.chromeBack = this.querySelector('[data-chrome-back]')
		this.drawer = this.closest('drawer-component')
		// Bound on the host, not panelsRoot: the back control lives in the chrome.
		this.addEventListener('click', this.onClick)
		this.addEventListener('keydown', this.onKeyDown)
		/* Safari unloads the page before a click-time class can paint.
		   Start the opening state on pointer/touch, cancel if they scroll. */
		this.addEventListener('pointerdown', this.onLeaveIntent, { capture: true, passive: true })
		this.addEventListener('touchstart', this.onLeaveIntent, { capture: true, passive: true })
		this.addEventListener('pointermove', this.onLeaveMove, { capture: true, passive: true })
		this.addEventListener('touchmove', this.onLeaveMove, { capture: true, passive: true })
		this.addEventListener('pointercancel', this.onLeaveCancel, { capture: true })
		this.addEventListener('touchcancel', this.onLeaveCancel, { capture: true })
		window.addEventListener('pageshow', this.onPageShow)
		if (this.drawer) {
			this.drawer.addEventListener('close', this.onDrawerClose)
			this.drawer.addEventListener('open', this.onDrawerOpen)
		}
		this.resetToRoot({ instant: true })
		// One CDN warm per tab session — not again on every page change.
		if (!this.hasSessionPrefetch()) this.schedulePrefetch()
	}

	disconnectedCallback() {
		this.removeEventListener('click', this.onClick)
		this.removeEventListener('keydown', this.onKeyDown)
		this.removeEventListener('pointerdown', this.onLeaveIntent, { capture: true })
		this.removeEventListener('touchstart', this.onLeaveIntent, { capture: true })
		this.removeEventListener('pointermove', this.onLeaveMove, { capture: true })
		this.removeEventListener('touchmove', this.onLeaveMove, { capture: true })
		this.removeEventListener('pointercancel', this.onLeaveCancel, { capture: true })
		this.removeEventListener('touchcancel', this.onLeaveCancel, { capture: true })
		window.removeEventListener('pageshow', this.onPageShow)
		this.drawer?.removeEventListener('close', this.onDrawerClose)
		this.drawer?.removeEventListener('open', this.onDrawerOpen)
		this.clearTouchScrollLock()
		if (this._prefetchTimer) window.clearTimeout(this._prefetchTimer)
		if (this._prefetchIdle && typeof window.cancelIdleCallback === 'function') {
			window.cancelIdleCallback(this._prefetchIdle)
		}
	}

	/**
	 * Block background page scroll on iOS when the gesture is not inside a
	 * panel that can actually scroll (chrome, short lists, rail gaps).
	 */
	onTouchMoveWhileOpen(event) {
		if (event.touches && event.touches.length > 1) return
		const target = event.target
		if (!(target instanceof Element)) {
			event.preventDefault()
			return
		}
		const panelScroll = target.closest('.f-mobile-nav__panel-scroll')
		if (
			panelScroll &&
			panelScroll.scrollHeight > panelScroll.clientHeight + 1
		) {
			return
		}
		event.preventDefault()
	}

	armTouchScrollLock() {
		if (this._touchLockOn) return
		this._touchLockOn = true
		document.addEventListener('touchmove', this.onTouchMoveWhileOpen, {
			passive: false,
		})
	}

	clearTouchScrollLock() {
		if (!this._touchLockOn) return
		this._touchLockOn = false
		document.removeEventListener('touchmove', this.onTouchMoveWhileOpen)
	}

	hasSessionPrefetch() {
		try {
			return window.sessionStorage.getItem('i9-mobile-nav-img-warm') === '1'
		} catch {
			return false
		}
	}

	markSessionPrefetch() {
		try {
			window.sessionStorage.setItem('i9-mobile-nav-img-warm', '1')
		} catch {
			/* private mode / blocked storage — page-local only */
		}
	}

	schedulePrefetch() {
		const run = () => this.prefetchAllImages()
		if (typeof window.requestIdleCallback === 'function') {
			this._prefetchIdle = window.requestIdleCallback(run, { timeout: 1800 })
		} else {
			this._prefetchTimer = window.setTimeout(run, 600)
		}
	}

	/**
	 * Hidden drawer trees skip lazy IntersectionObserver. Force every nav image
	 * into the HTTP cache once per session so later pages reuse disk cache.
	 */
	prefetchAllImages() {
		if (!this.panelsRoot || this.hasSessionPrefetch()) return
		this.markSessionPrefetch()
		const imgs = this.panelsRoot.querySelectorAll('img')
		imgs.forEach((img, index) => {
			img.loading = 'eager'
			img.fetchPriority = index < 6 ? 'high' : 'low'
			const url = img.currentSrc || img.src
			if (!url) return
			const warm = new Image()
			warm.decoding = 'async'
			warm.src = url
		})
	}

	getPanel(id) {
		return this.panelsRoot.querySelector(`[data-panel="${CSS.escape(id)}"]`)
	}

	onDrawerClose() {
		this.clearTouchScrollLock()
		this.resetToRoot({ instant: true })
	}

	onDrawerOpen() {
		this.armTouchScrollLock()
		// First open in this tab still warms; later pages skip the full pull.
		this.prefetchAllImages()
		const root = this.getPanel('root')
		if (root) this.warmPanelImages(root)
	}

	/** Promote imgs in the active panel so paint uses the warm cache immediately. */
	warmPanelImages(panel) {
		if (!panel) return
		const imgs = panel.querySelectorAll('img')
		imgs.forEach((img, index) => {
			img.loading = 'eager'
			img.fetchPriority = index === 0 ? 'high' : 'low'
			if (typeof img.decode === 'function') {
				img.decode().catch(() => {})
			}
		})
	}

	onClick(event) {
		const openBtn = event.target.closest('[data-open-panel]')
		if (openBtn && this.panelsRoot.contains(openBtn)) {
			event.preventDefault()
			this._lastTriggerWasKeyboard = event.detail === 0
			this.openPanel(openBtn.getAttribute('data-open-panel'))
			return
		}

		const backBtn = event.target.closest('[data-nav-back]')
		if (backBtn && this.contains(backBtn)) {
			event.preventDefault()
			this._lastTriggerWasKeyboard = event.detail === 0
			this.goBack()
		}
	}

	_eventElement(event) {
		const raw = event.target
		if (raw instanceof Element) return raw
		return raw && raw.parentElement instanceof Element ? raw.parentElement : null
	}

	_eventPoint(event) {
		if (event.touches && event.touches[0]) {
			return { x: event.touches[0].clientX, y: event.touches[0].clientY }
		}
		if (event.clientX != null) return { x: event.clientX, y: event.clientY }
		return null
	}

	_leaveLinkFromEvent(event) {
		if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null
		if (event.pointerType === 'mouse' && event.button != null && event.button !== 0) {
			return null
		}
		const target = this._eventElement(event)
		if (!target) return null
		if (target.closest('[data-open-panel], [data-nav-back], .f-drawer__close')) return null

		const link = target.closest('a[href]')
		if (!link || !this.contains(link)) return null

		const href = link.getAttribute('href')
		if (!href || href === '#' || href.startsWith('javascript:')) return null

		try {
			const next = new URL(link.href, window.location.href)
			const here = window.location
			if (
				next.origin === here.origin &&
				next.pathname === here.pathname &&
				next.search === here.search
			) {
				return null
			}
		} catch {
			return null
		}
		return link
	}

	/**
	 * Safari (especially iOS) unloads the document before a click-time class
	 * can paint. Start the leave state on pointer/touch; drop it if the finger scrolls.
	 */
	onLeaveIntent(event) {
		if (this.isLeaving) return
		if (event.type === 'touchstart' && event.pointerType) return
		const link = this._leaveLinkFromEvent(event)
		if (!link) return
		const point = this._eventPoint(event)
		this._leaveCandidate = {
			link,
			x: point ? point.x : 0,
			y: point ? point.y : 0,
		}
		this.beginLeave(link)
	}

	onLeaveMove(event) {
		const candidate = this._leaveCandidate
		if (!candidate || !this.isLeaving) return
		const point = this._eventPoint(event)
		if (!point) return
		const dx = point.x - candidate.x
		const dy = point.y - candidate.y
		if (dx * dx + dy * dy > 144) this.clearLeave()
	}

	onLeaveCancel() {
		if (this._leaveCandidate) this.clearLeave()
	}

	beginLeave(link) {
		this.isLeaving = true
		this.classList.add('is-navigating')
		this.setAttribute('aria-busy', 'true')
		link.classList.add('is-opening')
		link.closest('.f-mobile-nav__item')?.classList.add('is-opening-item')
		link.closest('.f-mobile-nav__rail')?.classList.add('is-opening-rail')

		/* Safari will not start a CSS animation added in the same turn as
		   navigation unless the layer is forced to paint first. */
		const bar = this.querySelector('.f-mobile-nav__leave-progress-bar')
		if (bar) {
			bar.style.webkitAnimation = 'none'
			bar.style.animation = 'none'
			void this.offsetHeight
			bar.style.webkitAnimation = ''
			bar.style.animation = ''
		} else {
			void this.offsetHeight
		}
	}

	clearLeave() {
		this._leaveCandidate = null
		if (!this.isLeaving) return
		this.isLeaving = false
		this.classList.remove('is-navigating')
		this.removeAttribute('aria-busy')
		this.querySelectorAll('.is-opening, .is-opening-item, .is-opening-rail').forEach((el) => {
			el.classList.remove('is-opening', 'is-opening-item', 'is-opening-rail')
		})
	}

	onPageShow(event) {
		if (event.persisted) this.clearLeave()
	}

	syncChrome() {
		if (!this.chromeBack) return
		this.chromeBack.hidden = this.stack.length <= 1
	}

	onKeyDown(event) {
		if (event.key === 'Escape' && this.stack.length > 1) {
			event.preventDefault()
			event.stopPropagation()
			this._lastTriggerWasKeyboard = true
			this.goBack()
		}
	}

	openPanel(panelId) {
		if (!panelId || this.isAnimating) return
		const next = this.getPanel(panelId)
		const current = this.getPanel(this.stack[this.stack.length - 1])
		if (!next || !current || next === current) return

		this.stack.push(panelId)
		this.syncChrome()
		this.transition(current, next, 'forward')
	}

	goBack() {
		if (this.stack.length <= 1 || this.isAnimating) return
		const currentId = this.stack.pop()
		const current = this.getPanel(currentId)
		const prev = this.getPanel(this.stack[this.stack.length - 1])
		if (!current || !prev) return
		this.syncChrome()
		this.transition(current, prev, 'back')
	}

	resetToRoot({ instant = false } = {}) {
		const panels = [...this.panelsRoot.querySelectorAll('[data-panel]')]
		panels.forEach((panel) => {
			const isRoot = panel.getAttribute('data-panel') === 'root'
			panel.classList.remove(
				'is-active',
				'is-entering',
				'is-leaving',
				'is-leaving-on-top',
				'is-enter-from-right',
				'is-enter-from-left',
				'is-leave-to-left',
				'is-leave-to-right'
			)
			if (isRoot) {
				panel.hidden = false
				panel.classList.add('is-active')
			} else {
				panel.hidden = true
			}
		})
		this.stack = ['root']
		this.isAnimating = false
		this.syncChrome()
		if (!instant) return
		this.getPanel('root')?.querySelector('.f-mobile-nav__panel-scroll')?.scrollTo?.(0, 0)
	}

	transition(fromPanel, toPanel, direction) {
		const duration = this.reduceMotion ? 0 : 320
		this.isAnimating = true

		toPanel.hidden = false
		this.warmPanelImages(toPanel)
		toPanel.classList.add(
			'is-entering',
			direction === 'forward' ? 'is-enter-from-right' : 'is-enter-from-left'
		)
		fromPanel.classList.add(
			'is-leaving',
			direction === 'forward' ? 'is-leave-to-left' : 'is-leave-to-right'
		)
		if (direction === 'back') {
			fromPanel.classList.add('is-leaving-on-top')
		}
		fromPanel.classList.remove('is-active')

		void toPanel.offsetWidth
		toPanel.classList.add('is-active')
		toPanel.classList.remove('is-enter-from-right', 'is-enter-from-left')

		const finish = () => {
			fromPanel.classList.remove(
				'is-leaving',
				'is-leaving-on-top',
				'is-leave-to-left',
				'is-leave-to-right'
			)
			fromPanel.hidden = true
			toPanel.classList.remove('is-entering')
			toPanel.querySelector('.f-mobile-nav__panel-scroll')?.scrollTo?.(0, 0)
			this.isAnimating = false

			const focusTarget = toPanel.querySelector(
				'.f-mobile-nav__link, .f-mobile-nav__view-all'
			)
			// Only auto-focus when the transition was driven by keyboard navigation.
			// On mouse/touch clicks, programmatic focus paints a thick black box on row 1.
			if (this._lastTriggerWasKeyboard) {
				focusTarget?.focus?.({ preventScroll: true })
			}
		}

		if (duration === 0) {
			finish()
			return
		}

		window.setTimeout(finish, duration)
	}
}

if (!customElements.get('mobile-nav-panels')) {
	customElements.define('mobile-nav-panels', MobileNavPanels)
}
